import React, { useState } from "react";
import useSWR from "swr";
import { Form, Formik, useFormikContext } from "formik";
import FormikRadio from "components/form/formik-radio";
import FormikCheckbox from "components/form/formik-checkbox";
import { buildPresetInitialValues } from "utils/preset-answer-helpers";
import * as Yup from "yup";
import { fetcherSimple } from "utils/api";
import DeleteCustomAnswerDialog from "./dialog/delete-custom-answer-dialog";

export default function PresetQuestionResponse(props) {
    const question = props.question;
    const responses = props.responses;
    const response = props.responses.preset;

    // Tracks whether the user confirmed overwriting an existing custom answer
    // when picking a preset in single-answer mode.
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [reallyDeleteCustom, setReallyDeleteCustom] = useState(false);

    const AutoSubmit = () => {
        const ctx = useFormikContext();

        React.useEffect(() => {
            if (
                (ctx.values.answer[0] || ctx.values.answer[0] === "") &&
                responses.custom.length > 0 &&
                responses.preset.length > 0 &&
                question.answer_type === "single"
            ) {
                setShowConfirmDialog(true);
            } else if (
                questionPresets &&
                ctx.dirty &&
                ctx.values &&
                !ctx.isSubmitting
            ) {
                ctx.submitForm();
            }
        }, [ctx.values]);
        return null;
    };

    const { data: questionPresets, error: questionPresetsError } = useSWR(
        () => [`/v2/response/question/${question.nanoid}/preset`, "GET"],
        ([path, method]) => fetcherSimple(path, method),
    );

    const handlePresetSubmit = async (values, actions) => {
        if (question.answer_type === "single") {
            if (!values.answer[0] || values.answer[0] === "") {
                return null;
            }
            // User declined the "this will replace your custom answer" dialog.
            if (
                responses.custom.length > 0 &&
                !reallyDeleteCustom &&
                actions.status &&
                actions.status.confirming
            ) {
                actions.setStatus({ confirming: false });
                actions.setValues(
                    new Array(questionPresets.preset_answers.length),
                );
                setShowConfirmDialog(false);
                return;
            }
            // Picking a preset when a custom answer exists: ask first.
            if (responses.custom.length > 0 && !reallyDeleteCustom) {
                actions.setStatus({ confirming: true });
                setShowConfirmDialog(true);
                return;
            }
            setReallyDeleteCustom(false);
            actions.setStatus({ confirming: false });
            const newAnswers = questionPresets.preset_answers.filter(
                (p) => values.answer[0] === p.nanoid,
            );
            props.setPresetOther(false);
            // Avoid an unnecessary state mutation when nothing changed.
            if (
                newAnswers.length !== response.length ||
                (newAnswers[0] && newAnswers[0].nanoid !== response[0].nanoid)
            ) {
                props.setAnswers({ preset: newAnswers, custom: [] });
            }
            actions.setStatus({ confirming: false });
            setShowConfirmDialog(false);
        } else {
            const newAnswers = questionPresets.preset_answers.filter((p) =>
                values.answer.some((ele) => ele === p.nanoid),
            );
            if (newAnswers.length !== response.length) {
                props.setAnswers({
                    preset: newAnswers,
                    custom: responses.custom,
                });
            }
        }
    };

    const validationSchemaPreset = Yup.array().of(Yup.string());

    return (
        <>
            {!!questionPresetsError && (
                <div>Error fetching preset answers!</div>
            )}
            {!!(questionPresets && response) && (
                <Formik
                    initialValues={{
                        answer: buildPresetInitialValues(
                            questionPresets.preset_answers,
                            response,
                            question.answer_type,
                        ),
                    }}
                    onSubmit={handlePresetSubmit}
                    validationSchema={validationSchemaPreset}
                    enableReinitialize
                >
                    {({ values, isValid, dirty }) => (
                        <Form className="font-body">
                            {questionPresets.preset_answers
                                .sort((a, b) => {
                                    return a.position - b.position;
                                })
                                .map((singlePreset, index) => (
                                    <div key={index}>
                                        {question.answer_type === "single" && (
                                            <FormikRadio
                                                className="font-body text-black"
                                                containerProps={{className: "shrink-0"}}
                                                id={`values.answer.${index}.nanoid`}
                                                name={`answer.0`}
                                                color="blue"
                                                label={singlePreset.text}
                                                value={singlePreset.nanoid}
                                                labelProps={{
                                                    className:
                                                        "text-black",
                                                }}
                                                type="radio"
                                            />
                                        )}
                                        {question.answer_type ===
                                            "multiple" && (
                                            <FormikCheckbox
                                                className="font-body text-black"
                                                containerProps={{className: "shrink-0"}}
                                                id={`values.answer.${index}.nanoid`}
                                                name={`answer`}
                                                color="blue"
                                                label={singlePreset.text}
                                                value={singlePreset.nanoid}
                                                type="checkbox"
                                                labelProps={{
                                                    className:
                                                        "text-black",
                                                }}
                                            />
                                        )}
                                    </div>
                                ))}
                            <DeleteCustomAnswerDialog
                                showDialog={showConfirmDialog}
                                setShowDialog={setShowConfirmDialog}
                                setReallyDeleteCustom={setReallyDeleteCustom}
                                question={question}
                                questionPresets={questionPresets}
                                handleClose={() =>
                                    setShowConfirmDialog(
                                        (showConfirmDialog) =>
                                            !showConfirmDialog,
                                    )
                                }
                            />
                            <AutoSubmit />
                        </Form>
                    )}
                </Formik>
            )}
        </>
    );
}
